import { createReducer, type Reducer } from '@reduxjs/toolkit';
import { castDraft, type Draft } from 'immer';
import { type SourceError } from 'js-slang/dist/errors/base';

import { logOut } from '../application/actions/CommonsActions';
import InterpreterActions from '../application/actions/InterpreterActions';
import {
  createDefaultWorkspace,
  defaultWorkspaceManager,
  type ErrorOutput,
  type InterpreterOutput,
  type NotificationOutput,
  type ResultOutput,
} from '../application/ApplicationTypes';
import {
  setEditorSessionId,
  setSessionDetails,
  setSharedbConnected,
  setUpdateUserRoleCallback,
} from '../collabEditing/CollabEditingActions';
import type { SourceActionType } from '../utils/ActionsHelper';
import { createContext } from '../utils/JsSlangHelper';
import { handleCseAndStepperActions } from './reducers/cseReducer';
import { handleDebuggerActions } from './reducers/debuggerReducer';
import { handleEditorActions } from './reducers/editorReducer';
import { handleReplActions } from './reducers/replReducer';
import WorkspaceActions from './WorkspaceActions';
import type { WorkspaceLocation, WorkspaceManagerState } from './WorkspaceTypes';

export const getWorkspaceLocation = (action: any): WorkspaceLocation => {
  return action.payload ? action.payload.workspaceLocation : 'assessment';
};

/**
 * Takes in a IWorkspaceManagerState and maps it to a new state. The
 * pre-conditions are that
 *   - There exists an IWorkspaceState in the IWorkspaceManagerState of the key
 *     `location`.
 *   - `location` is defined (and exists) as a property 'workspaceLocation' in
 *     the action's payload.
 */
export const WorkspaceReducer: Reducer<WorkspaceManagerState, SourceActionType> = (
  state = defaultWorkspaceManager,
  action,
) => {
  // state = oldWorkspaceReducer(state, action);
  state = newWorkspaceReducer(state, action);
  return state;
};

const newWorkspaceReducer = createReducer(defaultWorkspaceManager, builder => {
  handleEditorActions(builder);
  handleCseAndStepperActions(builder);
  handleReplActions(builder);
  handleDebuggerActions(builder);

  function applyInterpreterResultOutput(
    state: Draft<WorkspaceManagerState>,
    workspaceLocation: WorkspaceLocation,
    value: unknown,
    shouldStopRunning: boolean,
  ) {
    const tokens = state[workspaceLocation].tokenCount;
    const newOutputEntry: Partial<ResultOutput> = {
      type: 'result',
      value,
    };

    const lastOutput: InterpreterOutput = state[workspaceLocation].output.slice(-1)[0];
    let newOutput: InterpreterOutput[];

    if (lastOutput !== undefined && lastOutput.type === 'running') {
      const newOutputEntryWithLogs = {
        consoleLogs: lastOutput.consoleLogs,
        ...newOutputEntry,
      } as ResultOutput;
      const notificationOutputs: NotificationOutput[] = [];
      if (state[workspaceLocation].hasTokenCounter) {
        notificationOutputs.push({
          consoleLog: `This program has ${tokens} tokens.`,
          type: 'notification',
        });
      }
      const customNotification = state[workspaceLocation].customNotification;
      if (customNotification !== '') {
        notificationOutputs.push({
          consoleLog: customNotification,
          type: 'notification',
        });
      }
      newOutput = state[workspaceLocation].output
        .slice(0, -1)
        .concat([...notificationOutputs, newOutputEntryWithLogs]);
    } else {
      newOutput = state[workspaceLocation].output.concat({
        consoleLogs: [],
        ...newOutputEntry,
      } as ResultOutput);
    }

    state[workspaceLocation].output = newOutput;
    if (shouldStopRunning) {
      state[workspaceLocation].isRunning = false;
    }
  }

  function applyInterpreterErrorOutput(
    state: Draft<WorkspaceManagerState>,
    workspaceLocation: WorkspaceLocation,
    errors: SourceError[],
    shouldStopRunning: boolean,
    shouldStopDebugging: boolean,
  ) {
    const lastOutput: InterpreterOutput = state[workspaceLocation].output.slice(-1)[0];
    let newOutput: InterpreterOutput[];

    if (lastOutput !== undefined && lastOutput.type === 'running') {
      newOutput = state[workspaceLocation].output.slice(0, -1).concat({
        type: 'errors',
        errors,
        consoleLogs: lastOutput.consoleLogs,
      } as ErrorOutput);
    } else {
      newOutput = state[workspaceLocation].output.concat({
        type: 'errors',
        errors,
        consoleLogs: [],
      } as ErrorOutput);
    }

    state[workspaceLocation].output = newOutput;
    if (shouldStopRunning) {
      state[workspaceLocation].isRunning = false;
    }
    if (shouldStopDebugging) {
      state[workspaceLocation].isDebugging = false;
    }
  }

  builder
    .addCase(WorkspaceActions.setTokenCount, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].tokenCount = action.payload.tokenCount;
    })
    .addCase(WorkspaceActions.changeExecTime, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].execTime = action.payload.execTime;
    })
    .addCase(WorkspaceActions.endClearContext, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      // For some reason mutating the state directly results in type
      // errors, so we have to do it the old-fashioned way
      return {
        ...state,
        [workspaceLocation]: {
          ...state[workspaceLocation],
          context: createContext<WorkspaceLocation>(
            action.payload.library.chapter,
            action.payload.library.external.symbols,
            workspaceLocation,
            action.payload.library.variant,
            action.payload.library.languageOptions,
          ),
          globals: action.payload.library.globals,
          externalLibrary: action.payload.library.external.name,
        },
      };
    })
    .addCase(WorkspaceActions.changeExternalLibrary, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].externalLibrary = action.payload.newExternal;
    })
    .addCase(InterpreterActions.handleConsoleLog, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      /* Possible cases:
       * (1) state[workspaceLocation].output === [], i.e. state[workspaceLocation].output[-1] === undefined
       * (2) state[workspaceLocation].output[-1] is not RunningOutput
       * (3) state[workspaceLocation].output[-1] is RunningOutput */
      const lastOutput: InterpreterOutput =
        state[workspaceLocation].output[state[workspaceLocation].output.length - 1];
      let newOutput: InterpreterOutput[];

      if (lastOutput === undefined || lastOutput.type !== 'running') {
        // New block of output.
        newOutput = state[workspaceLocation].output.concat({
          type: 'running',
          consoleLogs: [...action.payload.logString],
        });
      } else {
        const updatedLastOutput = {
          type: lastOutput.type,
          consoleLogs: lastOutput.consoleLogs.concat(action.payload.logString),
        };
        newOutput = state[workspaceLocation].output.slice(0, -1);
        newOutput.push(updatedLastOutput);
      }

      state[workspaceLocation].output = newOutput;
    })
    .addCase(logOut, (state, action) => {
      // Preserve the playground workspace even after log out
      const playgroundWorkspace = state.playground;
      return castDraft({
        ...defaultWorkspaceManager,
        playground: playgroundWorkspace,
      });
    })
    .addCase(WorkspaceActions.enableTokenCounter, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].hasTokenCounter = true;
    })
    .addCase(WorkspaceActions.disableTokenCounter, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].hasTokenCounter = false;
    })
    .addCase(WorkspaceActions.evalEditor, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].isRunning = true;
      state[workspaceLocation].isDebugging = false;
    })
    .addCase(InterpreterActions.appendInterpreterResult, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      applyInterpreterResultOutput(state, workspaceLocation, action.payload.value, false);
    })
    .addCase(InterpreterActions.evalInterpreterSuccess, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      applyInterpreterResultOutput(state, workspaceLocation, action.payload.value, true);
    })
    .addCase(InterpreterActions.evalTestcaseSuccess, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const testcase = state[workspaceLocation].editorTestcases[action.payload.index];
      testcase.result = action.payload.value;
      testcase.errors = undefined;
      state[workspaceLocation].isRunning = false;
    })
    .addCase(InterpreterActions.evalTestcaseFailure, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const testcase = state[workspaceLocation].editorTestcases[action.payload.index];
      testcase.result = undefined;
      testcase.errors = action.payload.value;
    })
    .addCase(InterpreterActions.appendInterpreterError, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      applyInterpreterErrorOutput(state, workspaceLocation, action.payload.errors, false, false);
    })
    .addCase(InterpreterActions.evalInterpreterError, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      applyInterpreterErrorOutput(state, workspaceLocation, action.payload.errors, true, true);
    })
    .addCase(InterpreterActions.setIsRunning, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].isRunning = action.payload.isRunning;
    })
    .addCase(InterpreterActions.setIsWaitingForInput, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].isWaitingForInput = action.payload.isWaitingForInput;
    })
    /**
     * Called to signal the end of an interruption,
     * i.e called after the interpreter is told to stop interruption,
     * to cause UI changes.
     */
    .addCase(InterpreterActions.endInterruptExecution, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      /**
       * Set the isRunning property of the
       * context to false, to ensure a re-render.
       * Also in case the async js-slang interrupt()
       * function does not finish interrupting before
       * this action is called.
       */
      state[workspaceLocation].isRunning = false;
      state[workspaceLocation].isDebugging = false;
    })
    .addCase(WorkspaceActions.resetTestcase, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const testcase = state[workspaceLocation].editorTestcases[action.payload.index];
      testcase.result = undefined;
      testcase.errors = undefined;
    })
    /**
     * Resets the workspace to default settings,
     * including the js-slang Context. Apply
     * any specified settings (workspaceOptions)
     */
    .addCase(WorkspaceActions.resetWorkspace, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      // For some reason mutating the state directly results in type
      // errors, so we have to do it the old-fashioned way
      return {
        ...state,
        [workspaceLocation]: {
          ...state[workspaceLocation],
          ...createDefaultWorkspace(workspaceLocation),
          ...action.payload.workspaceOptions,
        },
      };
    })
    /**
     * Updates workspace without changing anything
     * which has not been specified
     */
    .addCase(WorkspaceActions.updateWorkspace, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      // For some reason mutating the state directly results in type
      // errors, so we have to do it the old-fashioned way
      return {
        ...state,
        [workspaceLocation]: {
          ...state[workspaceLocation],
          ...action.payload.workspaceOptions,
        },
      };
    })
    .addCase(WorkspaceActions.increaseRequestCounter, state => {
      state.grading.requestCounter += 1;
    })
    .addCase(WorkspaceActions.decreaseRequestCounter, state => {
      state.grading.requestCounter = Math.max(0, state.grading.requestCounter - 1);
    })
    .addCase(setEditorSessionId, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].editorSessionId = action.payload.editorSessionId;
    })
    .addCase(WorkspaceActions.setGradingHasLoadedBefore, (state, action) => {
      state.grading.hasLoadedBefore = action.payload;
    })
    .addCase(setSessionDetails, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      return {
        ...state,
        [workspaceLocation]: {
          ...state[workspaceLocation],
          sessionDetails: {
            ...state[workspaceLocation].sessionDetails,
            ...action.payload.sessionDetails,
          },
        },
      };
    })
    .addCase(WorkspaceActions.setIsEditorReadonly, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].isEditorReadonly = action.payload.isEditorReadonly;
    })
    .addCase(setSharedbConnected, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].sharedbConnected = action.payload.connected;
    })
    .addCase(WorkspaceActions.updateSubmissionsTableFilters, (state, action) => {
      state.grading.submissionsTableFilters = action.payload.filters;
    })
    .addCase(WorkspaceActions.updateAllColsSortStates, (state, action) => {
      state.grading.allColsSortStates = action.payload.sortStates;
    })
    .addCase(WorkspaceActions.updateCurrentAssessmentId, (state, action) => {
      state.assessment.currentAssessment = action.payload.assessmentId;
      state.assessment.currentQuestion = action.payload.questionId;
    })
    .addCase(WorkspaceActions.updateCurrentSubmissionId, (state, action) => {
      state.grading.currentSubmission = action.payload.submissionId;
      state.grading.currentQuestion = action.payload.questionId;
    })
    .addCase(WorkspaceActions.updateHasUnsavedChanges, (state, action) => {
      // For some reason mutating the state directly results in type
      // errors, so we have to do it the old-fashioned way
      const workspaceLocation = getWorkspaceLocation(action);
      return {
        ...state,
        [workspaceLocation]: {
          ...state[workspaceLocation],
          hasUnsavedChanges: action.payload.hasUnsavedChanges,
        },
      };
    })
    .addCase(WorkspaceActions.updateSublanguage, (state, action) => {
      // TODO: Mark for removal
      const { chapter, variant } = action.payload.sublang;
      state.playground.context.chapter = chapter;
      state.playground.context.variant = variant;
    })
    .addCase(WorkspaceActions.notifyProgramEvaluated, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const debuggerContext = state[workspaceLocation].debuggerContext;
      debuggerContext.result = action.payload.result;
      debuggerContext.lastDebuggerResult = action.payload.lastDebuggerResult;
      debuggerContext.code = action.payload.code;
      debuggerContext.context = castDraft(action.payload.context);
      debuggerContext.workspaceLocation = action.payload.workspaceLocation;
    })
    .addCase(WorkspaceActions.toggleUsingUpload, (state, action) => {
      const { workspaceLocation } = action.payload;
      if (workspaceLocation === 'playground' || workspaceLocation === 'sicp') {
        state[workspaceLocation].usingUpload = action.payload.usingUpload;
      }
    })
    .addCase(WorkspaceActions.uploadFiles, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      if (workspaceLocation === 'playground' || workspaceLocation === 'sicp') {
        state[workspaceLocation].files = action.payload.files;
      }
    })
    .addCase(WorkspaceActions.updateLastDebuggerResult, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].lastDebuggerResult = action.payload.lastDebuggerResult;
    })
    .addCase(setUpdateUserRoleCallback, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].updateUserRoleCallback = action.payload.updateUserRoleCallback;
    })
    .addCase(WorkspaceActions.fetchVersionHistory, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].versionHistory.isLoading = true;
    })
    .addCase(WorkspaceActions.receiveVersionHistory, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].versionHistory.versions = action.payload.versions;
      state[workspaceLocation].versionHistory.isLoading = false;
    })
    .addCase(WorkspaceActions.selectVersion, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].versionHistory.selectedVersion = action.payload.version;
      state[workspaceLocation].versionHistory.selectedVersionCode = null;
      state[workspaceLocation].versionHistory.isLoadingCode = action.payload.version !== null;
    })
    .addCase(WorkspaceActions.receiveVersionCode, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const versionHistory = state[workspaceLocation].versionHistory;
      if (versionHistory.selectedVersion?.id !== action.payload.versionId) {
        return;
      }
      versionHistory.selectedVersionCode = action.payload.code;
      versionHistory.isLoadingCode = false;
    })
    .addCase(WorkspaceActions.restoreVersion, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const workspace = state[workspaceLocation];
      if (workspace.activeEditorTabIndex !== null) {
        workspace.editorTabs[workspace.activeEditorTabIndex].value = action.payload.code;
      }
    })
    .addCase(WorkspaceActions.toggleHistoryPanel, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].versionHistory.isHistoryPanelOpen =
        !state[workspaceLocation].versionHistory.isHistoryPanelOpen;
    })
    .addCase(WorkspaceActions.nameVersion, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      const version = state[workspaceLocation].versionHistory.versions.find(
        v => v.id === action.payload.versionId,
      );
      if (version) {
        version.name = action.payload.name;
      }
    })
    .addCase(WorkspaceActions.updateSaveStatus, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].saveStatus = action.payload.saveStatus;
    })
    .addCase(WorkspaceActions.setIsAutoSaving, (state, action) => {
      const workspaceLocation = getWorkspaceLocation(action);
      state[workspaceLocation].versionHistory.isAutoSaving = action.payload.isAutoSaving;
    });
});
